import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import {
  useDataProvider,
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';

import { useNavigate } from 'react-router-dom';
import { createSchema } from '../../schemas';
import validationSchema from './validations';

function IndustryCreate() {
  const { formatMessage } = useLocalization();
  const createFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, get, getApiUrl } = useDataProvider(provider);
  const [formErrors, setFormErrors] = useState(null);
  const store = useStore();
  const storeName = 'industryFormStore';

  const postApiUrl = `${getApiUrl()}/sxcl/v1/industries`;

  const preProcessRequestData = data => {
    const { status, ...other } = data;
    return {
      status: status === 'true',
      ...other,
    };
  };

  const submitCreateForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    store.uiState[storeName].set(requestData);
    const response = await post(postApiUrl, { ...requestData });
    if (response?.ok) {
      store.uiState[storeName].set({});
      snackbar.showMessage(
        formatMessage({
          id: 'INDUSTRY_CREATE_SUCCESS',
          defaultMessage: 'Industry was saved successfully',
        }),
      );
      createFormRef.current?.resetAndNavigate(
        `/industries/edit/${response?.data?.id}`,
      );
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'INDUSTRY_CREATE_FAILURE',
          defaultMessage: 'Error: Could not save the industry',
        }),
      });
    }
  };

  const formProps = {
    customComponents: FormFields,
    schema: createSchema,
    onSubmit: submitCreateForm,
    ref: createFormRef,
    initialValues: store.uiState[storeName].get(),
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'INDUSTRIES_BREADCRUMB',
        defaultMessage: 'Industries',
      }),
      icon: IconLibrary.Industries,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/industries');
      },
    },
    {
      name: formatMessage({
        id: 'CREATE_INDUSTRY_BREADCRUMB',
        defaultMessage: 'Create Industry',
      }),
      icon: IconLibrary.Industry,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'EDIT_INDUSTRIES_STACKABLE_BUTTON',
        defaultMessage: 'List Industries',
      }),
      Icon: IconLibrary.Industries,
      variant: 'outlined',
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/industries');
      },
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'INDUSTRY_CREATE_HEADING',
          defaultMessage: 'Create Industry',
        })}>
        <Box>
          <CRUDComponent
            errors={formErrors}
            type={CRUD_COMPONENT_TYPE.FORM}
            formProps={formProps}>
            <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => createFormRef.current?.submit()}>
                {formatMessage({
                  id: 'INDUSTRY_CREATE_SAVE_BUTTON',
                  defaultMessage: 'Save Industry',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => createFormRef.current?.reset()}>
                {formatMessage({
                  id: 'INDUSTRY_CREATE_RESET_BUTTON',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </Box>
          </CRUDComponent>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(IndustryCreate);
